import { useState, useContext, useEffect, useCallback } from 'react';
import UserTable from '../components/users/UserTable';
import { Box, Container, Modal } from '@mui/material';
import { Select, Button, TextInput } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faDownload, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { getCompanyDepartment, getAllUsers, addUserDepartment, editUserDepartment, deleteUser, deleteMultipleUser, getCompanyUsers,getCompanies } from '../apis/axios';
import AuthContext from '../AuthContext';

function Users() {
  const auth = useContext(AuthContext);
  // State for users
  const [users, setUsers] = useState([]);
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const[companyData,setCompanyData]=useState([]);
  const [company,setCompany]=useState('');

  const fetchAllUsers = async () => {
    try {
      if (auth.userType === 'sa' && !auth.admin) {
        const response = await getAllUsers();
        //console.log(response);
        setData(response.data);
        setUsers(response.data);
        return;
      } else {
        const response = await getCompanyUsers({ company_id: auth.admin });
        //console.log(response);
         setData(response.data);
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, [auth.userType, auth.admin]);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await getCompanyDepartment(auth.admin);
      if (response && Array.isArray(response.data)) {
        const formattedData = response.data.map(dept => ({
          value: dept.id,
          label: dept.name,
        }));
        setData(formattedData);
      } else {
        console.error('Invalid response format for departments:', response.data);
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setData([]);
    }
  }, [auth.admin]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await getCompanies();
      if (response && Array.isArray(response.data)) {
        const formattedData = response.data.map(dept => ({
          value: Number(dept.id),
          label: dept.name,
        }));
        setCompanyData(formattedData);
      } else {
        console.error('Invalid response format for departments:', response.data);
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setData([]);
    }
  }, []);
 useEffect(() => {
    if (auth.admin) {
      fetchDepartments();
    }
    if(auth.userType==='antima'){
      fetchCompanies();
    }
  }, [auth.admin, fetchDepartments,auth.userType,fetchCompanies]);

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    username: '',
    password: '',

  });
  const [department, setDepartment] = useState('');
  const [userType, setUserType] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Define user type options and filter based on logged-in user's type
  const userTypeOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'antima', label: 'Antima Admin' },
    { value: 'installer', label: 'Installer' },
  ];

  // Filter out 'Super Admin' option if logged-in user is 'ad' (Admin) and not editing
  const filteredUserTypeOptions = auth.userType === 'admin' && !isEditing
    ? userTypeOptions.filter(option => option.value !== 'antima')
    : userTypeOptions;

  // Function to export data as CSV
  const exportToCSV = (data, filename) => {
    const headers = ['Name', 'Username', 'User Type', 'Department', 'Password'];
    const rows = data.map(item => [
      item.name,
      item.username,
      item.userType,
      item.department,
      item.password || '',
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.click();
    URL.revokeObjectURL(url);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle form submission for adding a new user
  const handleAddUser = async () => {
    const newUser = {
      name: formData.name,
      username: formData.username,
      user_type: userType,
      department_id: department,
      password: formData.password,
      company_id:company
    };
    //console.log(newUser);
    try {
      const res = await addUserDepartment(newUser);
      await fetchAllUsers();
      closeModal();
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };

  // Handle form submission for updating an existing user
  const handleUpdateUser = async () => {
    const ediUser = {
      id: formData.id,
      name: formData.name,
      username: formData.username,
      user_type: userType,
      department_id: department,
      password: formData.password,
      company_id:company
    };
    try {
      const res = await editUserDepartment(ediUser);
      //console.log(res);
      await fetchAllUsers();
      closeModal();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  // Handle edit user
  const handleEditUser = (user) => {
    //console.log(user);
    setFormData({
      id: user.id,
      name: user.name,
      username: user.username,
      password: user.password || '',
    });
    setDepartment(user.department ? String(user.department) : '');
    setUserType(user.user_type);
    setCompany(user.company_id)
    setIsEditing(true);
    setOpenModal(true);
  };

  // Handle single user deletion
  const handleDeleteUser = async (userId) => {
    //console.log(userId);
    try {
      const res = await deleteUser({ id: userId });
      //console.log(res);
      await fetchAllUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (!users || users.length === 0) return;

    try {
      if (action === 'deleteAll') {
        const userIds = users.map(user => user.id);
        await deleteMultipleUser({ ids: userIds });
        await fetchAllUsers();
      } else if (action === 'deleteSelected') {
        if (selectedRows.length === 0) {
          //console.log('No users selected for deletion');
          return;
        }
        await deleteMultipleUser({ ids: selectedRows });
        await fetchAllUsers();
        setSelectedRows([]);
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  // Close modal and reset form
  const closeModal = () => {
    setOpenModal(false);
    setIsEditing(false);
    setFormData({
      id: null,
      name: '',
      username: '',
      password: '',
    });
    setDepartment('');
    setUserType('');
    setShowPassword(false);
  };

  // Ripple effect handler
  const handleRippleEffect = (e) => {
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
    ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
    ripple.classList.add('export-button-ripple');

    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  };

  return (
    <Box display="flex" sx={{ maxWidth: 'none', justifyContent: 'center' }}>
      <Box flexGrow={1} ml={{ sm: 0 }} sx={{ maxWidth: 'none' }}>
        <Container
          className="py-4"
          sx={{
            maxWidth: 'none !important',
            overflowX: 'hidden',
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <div style={{ margin: '0 auto', width: '75.5vw', maxWidth: '75.5vw' }}>
            {/* Header and Buttons */}
            <div className="md:flex-row lg:flex-row xl:flex-row flex justify-between items-center mb-4 flex-col ">
              <div>
                <h1
                  style={{
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '32px',
                    fontWeight: 700,
                    color: '#202224',
                  }}
                >
                  Users
                </h1>
              </div>
              <div className="flex  gap-4">
                
                <Button
                  size="sm"
                  variant="filled"
                  className="export-button"
                  styles={{
                    root: {
                      width: '120px',
                      height: '36px',
                      backgroundColor: '#FF7A00',
                      borderRadius: '20px',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                      '&:hover': {
                        backgroundColor: '#FF7A00',
                      },
                    },
                    label: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    },
                  }}
                  onClick={(e) => {
                    handleRippleEffect(e);
                    exportToCSV(users, 'users.csv');
                  }}
                >
                  <FontAwesomeIcon
                    icon={faDownload}
                    style={{
                      height: '10px',
                      width: '10px',
                      color: '#fff',
                    }}
                  />
                  <span>Export</span>
                </Button>
                <Button
                  size="sm"
                  variant="filled"
                  className="export-button"
                  styles={{
                    root: {
                      width: '120px',
                      height: '36px',
                      backgroundColor: '#FF7A00',
                      borderRadius: '20px',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                      '&:hover': {
                        backgroundColor: '#FF7A00',
                      },
                    },
                    label: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    },
                  }}
                  onClick={(e) => {
                    handleRippleEffect(e);
                    setOpenModal(true);
                    setIsEditing(false);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    style={{
                      height: '10px',
                      width: '10px',
                      color: '#fff',
                    }}
                  />
                  <span>Add User</span>
                </Button>
              </div>
            </div>

            {/* User Table */}
            <div>
              <UserTable
                data={users}
                setUsers={setUsers}
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
                onSelectionChange={setSelectedRows}
                userType={auth.userType}
              />
            </div>
          </div>

          {/* Add/Edit User Modal */}
          <Modal
            open={openModal}
            onClose={closeModal}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                backgroundColor: '#F7F7F7',
                padding: '20px 30px',
                borderRadius: '8px',
                width: '90vw',
                maxWidth: '900px',
                position: 'relative',
                maxHeight: { xs: '90vh', sm: 'none' },
                overflowY: { xs: 'auto', sm: 'hidden' },
                boxShadow: '6px 6px 6px #00000040',
              }}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-4">
                <h1
                  style={{
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '30px',
                    fontWeight: 700,
                    color: '#202224',
                  }}
                >
                  {isEditing ? 'Edit User' : 'Add User'}
                </h1>
                <FontAwesomeIcon
                  icon={faTimes}
                  style={{
                    height: '17px',
                    width: '17px',
                    color: '#FF7A00',
                    cursor: 'pointer',
                  }}
                  onClick={closeModal}
                />
              </div>

             
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Name */}
                <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Name*
                  </label>
                  <TextInput
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., John Doe"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '200px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </div>
                {/* Username */}
                <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Username*
                  </label>
                  <TextInput
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    placeholder="e.g., johndoe"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '200px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Password */}
                <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Set Password*
                  </label>
                  <TextInput
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter password"
                    type={showPassword ? 'text' : 'password'}
                    rightSection={
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                        onClick={() => setShowPassword(prev => !prev)}
                        style={{ cursor: 'pointer', color: '#595C60', height: '16px', width: '16px' }}
                      />
                    }
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '200px' },
                        height: { xs: '36px', sm: '40px' },
                        paddingRight: '30px',
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </div>
                {/* User Type */}
                <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    User Type*
                  </label>
                  <Select
                    value={userType}
                    onChange={(value) => {
                      setUserType(value);
                      handleInputChange('userType', value);
                    }}
                    data={filteredUserTypeOptions}
                    placeholder="Select User Type"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '200px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                      item: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        '&[data-selected]': {
                          backgroundColor: '#FF6B00',
                          color: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: '#FF6B00',
                            color: '#FFFFFF',
                          },
                        },
                        '&:hover': {
                          backgroundColor: '#FFF3E5',
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Department */}
                {auth.userType==='admin' &&(
                  <>
                  <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Department*
                  </label>
                  <Select
                    value={department}
                    onChange={(value) => {
                      setDepartment(value);
                      handleInputChange('userDepartment', value);
                    }}
                    data={data}
                    placeholder="Select Department"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '200px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                      item: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        '&[data-selected]': {
                          backgroundColor: '#FF6B00',
                          color: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: '#FF6B00',
                            color: '#FFFFFF',
                          },
                        },
                        '&:hover': {
                          backgroundColor: '#FFF3E5',
                        },
                      },
                    }}
                  />
                </div>
                  </>
                )}
                {
                  auth.userType==="antima" &&(
                  <>
                  <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Company*
                  </label>
                  <Select
                    value={company}
                    onChange={(value) => {
                      setCompany(value);
                    }}
                    data={companyData}
                    placeholder="Select Company "
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '200px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                      item: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        '&[data-selected]': {
                          backgroundColor: '#FF6B00',
                          color: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: '#FF6B00',
                            color: '#FFFFFF',
                          },
                        },
                        '&:hover': {
                          backgroundColor: '#FFF3E5',
                        },
                      },
                    }}
                  />
                </div>
                  </>)
                }
                {/* Empty div to maintain layout */}
                <div className="flex flex-col flex-1"></div>
              </div>

              {/* Add/Update User Button */}
              <div className="flex justify-end">
                <Button
                  className="export-button"
                  styles={{
                    root: {
                      width: { xs: '100px', sm: '120px' },
                      height: { xs: '32px', sm: '36px' },
                      backgroundColor: '#FF7A00',
                      borderRadius: '20px',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                      '&:hover': {
                        backgroundColor: '#FF7A00',
                      },
                    },
                    label: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '12px', sm: '14px' },
                      fontWeight: 500,
                      color: '#fff',
                    },
                  }}
                  onClick={(e) => {
                    handleRippleEffect(e);
                    isEditing ? handleUpdateUser() : handleAddUser();
                  }}
                  disabled={
                    !formData.name ||
                    !formData.username ||
                    !formData.password
                  }
                >
                  {isEditing ? 'Update User' : 'Add User'}
                </Button>
              </div>
            </div>
          </Modal>
        </Container>
      </Box>

      {/* Ripple Effect Styles */}
      <style>
        {`
          .export-button {
            position: relative;
            overflow: hidden;
          }
          .export-button-ripple {
            position: absolute;
            border-radius: 50%;
            background-color: rgba(255, 255, 255, 0.4);
            transform: scale(0);
            animation: ripple 0.6s linear;
            pointer-events: none;
          }
          @keyframes ripple {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }
        `}
      </style>
    </Box>
  );
}

export default Users;